import React, { useRef, useState } from "react";
import { SurfaceType } from "utils/analytics/analyticsConstants";
import VisibilityTracker from "ui/VisibilityTracker";
import {
  useAnalytics,
  ActionType,
  AnalyticsProvider,
} from "utils/analytics/AnalyticsContext";
import Modal from "ui/Modal";
import Form, { FocusFieldError } from "ui/Form";
import {
  useCreateZendeskTicketMutation,
  CreateZendeskTicketMutationVariables,
} from "./GetHelpModal.generated";
import Typography from "ui/Typography";
import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import Link from "ui/Link";
import CollapsibleContainer from "ui/CollapsibleContainer";
import { ReactComponent as ThumbsUpSVG } from "images/thumbs_up_mint.svg";
import {
  $ModalDialog,
  $ArticlesContainer,
  $HelpCenterText,
  $ArticlesHeading,
  $AdditionalHelpContainer,
  $FormContainer,
  $LightBulb,
  $Inline,
  $IntegrationResourcesText,
  $AdditionalHelpHeading,
  $FormInput,
  $FormSelect,
  $FormTextarea,
  $SuccessMessageContainer,
  $SuccessMessageHeading,
} from "./styled-components";
import SunnySpinner from "ui/SunnySpinner";
import { HelpLinks } from "utils/helpLinks";

export type RequestType = CreateZendeskTicketMutationVariables["requestType"];

const REQUEST_TYPES: {
  [RT in RequestType]: string;
} = {
  user_confusion: "How To",
  bug: "Bug or Technical Error",
  feature_request: "Feature Request",
  lead: "Lead or General Interest",
  data_request: "Data Request",
  content_report: "Content Report",
  other_request: "Other",
};

const CREATE_SUPPORT_REQUEST_SCHEMA = yup.object().shape({
  name: yup.string().max(50).required("Please enter a name"),
  email: yup
    .string()
    .email("Must be a valid email")
    .required("Please enter an email"),
  subject: yup.string().max(100).required("Please enter a subject"),
  comment: yup.string().max(2000).required("Please enter a message"),
});

type GetHelpModalProps = {
  userType: string;
  email: string;
  name: string;
  subject?: string;
  requestType?: RequestType;
  show: boolean;
  isLoadingArticles: boolean;
  articles: Article[];
  helpFormInitiallyExpanded?: boolean;
  onClose: () => void;
};

export interface Article {
  title: string;
  link: string;
}

const StudentArticles: Article[] = [
  {
    title: "How does a student respond to their reflection question?",
    link: HelpLinks.STUDENT_RESPOND_TO_REFLECTION,
  },
  {
    title: "How does a student see a response to their first question?",
    link: HelpLinks.STUDENT_VIEW_TEACHER_RESPONSES,
  },
  {
    title: "What should I do if my camera isn’t working?",
    link: HelpLinks.CAMERA_ISSUES,
  },
];

const TeacherArticles: Article[] = [
  {
    title: "What should I do if my camera isn’t working?",
    link: HelpLinks.CAMERA_ISSUES,
  },
  {
    title: "How do I send my students a reflection question?",
    link: HelpLinks.SEND_REFLECTION,
  },
];

function renderArticleLinks(
  userType: string,
  relevantArticles: Article[],
  isLoadingArticles: boolean,
) {
  let articles;

  if (isLoadingArticles) {
    return <SunnySpinner />;
  }

  if (relevantArticles?.length) {
    articles = relevantArticles;
  } else if (userType === "Teacher") {
    articles = TeacherArticles;
  } else {
    articles = StudentArticles;
  }

  return articles.map(({ title, link }) => (
    <$Inline key={title} alignY="center" space="Size2xs">
      <$LightBulb role="presentation" />
      <Link
        href={link}
        opensInNewTab
        analyticsName="clicked_help_center_article_link"
        analyticsSurface={SurfaceType.GET_HELP}
      >
        {title}
      </Link>
    </$Inline>
  ));
}

function GetHelpModal({
  show,
  userType,
  email,
  name,
  subject = "",
  requestType = "user_confusion",
  articles,
  isLoadingArticles,
  helpFormInitiallyExpanded = false,
  onClose,
}: GetHelpModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [createZendeskTicketMutation] = useCreateZendeskTicketMutation();
  const { trackEvent } = useAnalytics("GetHelpModal");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    onClose();
    setSubmitted(false);
  };

  async function handleSubmit(
    variables: CreateZendeskTicketMutationVariables,
    { setStatus }: FormikHelpers<CreateZendeskTicketMutationVariables>,
  ) {
    try {
      await createZendeskTicketMutation({
        variables,
      });

      trackEvent({
        eventName: "submitted_help_request",
        eventType: ActionType.CLICK_BUTTON,
        analyticsSurface: SurfaceType.GET_HELP,
        extraProperties: {},
      });

      setSubmitted(true);
    } catch {
      setStatus({
        error: "Sorry, something went wrong, please try again.",
      });
    }
  }

  // Fields in the in-app help form should be the same as those
  // in the "Submit a request" form in help.along.org
  function renderHelpForm() {
    return (
      <Formik
        initialValues={{
          name,
          email,
          requestType,
          subject,
          comment: "",
        }}
        validationSchema={CREATE_SUPPORT_REQUEST_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          values,
          touched,
          errors,
          status,
        }) => (
          <form noValidate onSubmit={handleSubmit} data-testid="form">
            <FocusFieldError>
              <Modal.Body>
                <Form.Group controlId="name">
                  <Form.Label>
                    <Typography size="subheading" as="span">
                      Name
                    </Typography>
                  </Form.Label>
                  <$FormInput
                    name="name"
                    type="text"
                    ref={inputRef}
                    placeholder="Your Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && !!errors.name}
                    required={true}
                    aria-invalid={!!errors.name}
                    aria-describedby="nameError"
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    id="nameError"
                    aria-live="polite"
                  >
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="email">
                  <Form.Label>
                    <Typography size="subheading" as="span">
                      Email
                    </Typography>
                  </Form.Label>
                  <$FormInput
                    type="email"
                    name="email"
                    value={values.email}
                    placeholder="email@example.com"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    required={true}
                    aria-invalid={!!errors.email}
                    aria-describedby="emailError"
                  />
                  <Form.Text className="text-muted">
                    We will never share your email with anyone else.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid" id="emailError">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="requestType">
                  <Form.Label>
                    <Typography size="subheading" as="span">
                      What can we help you with?
                    </Typography>
                  </Form.Label>
                  <$FormSelect
                    name="requestType"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.requestType && !!errors.requestType}
                    required={true}
                    aria-invalid={!!errors.requestType}
                    aria-describedby="requestTypeError"
                  >
                    {Object.entries(REQUEST_TYPES).map(([value, text]) => {
                      return (
                        <option
                          key={value}
                          value={value}
                          selected={value === requestType}
                        >
                          {text}
                        </option>
                      );
                    })}
                  </$FormSelect>
                </Form.Group>

                <Form.Group controlId="subject">
                  <Form.Label>
                    <Typography size="subheading" as="span">
                      Subject
                    </Typography>
                  </Form.Label>
                  <$FormInput
                    type="text"
                    placeholder="Subject"
                    name="subject"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.subject && !!errors.subject}
                    required={true}
                    aria-invalid={!!errors.subject}
                    aria-describedby="subjectError"
                  />
                  <Form.Control.Feedback type="invalid" id="subjectError">
                    {errors.subject}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="comment">
                  <Form.Label>
                    <Typography size="subheading" as="span">
                      Description
                    </Typography>
                  </Form.Label>
                  <$FormTextarea
                    name="comment"
                    rows={3}
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.comment && !!errors.comment}
                    required={true}
                    aria-invalid={!!errors.comment}
                    aria-describedby="commentError"
                  />
                  <Form.Control.Feedback type="invalid" id="commentError">
                    {errors.comment}
                  </Form.Control.Feedback>
                </Form.Group>
                {status?.error && <p className="text-danger">{status.error}</p>}
              </Modal.Body>
              <Modal.Footer>
                <Modal.PrimaryButton
                  analyticsName="clicked_submit_help_request"
                  analyticsSurface={SurfaceType.GET_HELP}
                  requestInProgress={isSubmitting}
                  /* The handleSubmit method signature doesn't match the buttons onClick since one
                     expects a form event (or undefined) and the other is a mouse click. */
                  onClick={() => handleSubmit()}
                >
                  Submit
                </Modal.PrimaryButton>
              </Modal.Footer>
            </FocusFieldError>
          </form>
        )}
      </Formik>
    );
  }

  const successText = (
    <Modal.Body>
      <$SuccessMessageContainer>
        <ThumbsUpSVG role="presentation" />
        <$SuccessMessageHeading>Thank you!</$SuccessMessageHeading>
        <Typography size="bodySansSerif">
          Someone from Along will get back to you as soon as possible.
        </Typography>
      </$SuccessMessageContainer>
    </Modal.Body>
  );

  return (
    <AnalyticsProvider surface={SurfaceType.GET_HELP}>
      <VisibilityTracker analyticsName="viewed_get_help_modal">
        <Modal
          show={show}
          close={{
            analyticsName: "clicked_close_get_help_modal",
            onClick: handleClose,
          }}
          dialogAs={$ModalDialog}
        >
          <Modal.Header>
            <Typography as="h1" size="h2">
              How can we help?
            </Typography>
          </Modal.Header>
          <$ArticlesContainer>
            <$ArticlesHeading size="h4">Popular questions</$ArticlesHeading>
            {renderArticleLinks(userType, articles, isLoadingArticles)}
            <$HelpCenterText size="bodySansSerif">
              And many more in the{" "}
              <Link
                href="https://help.along.org/hc/en-us"
                opensInNewTab
                analyticsName="clicked_help_center_link"
                analyticsSurface={SurfaceType.GET_HELP}
              >
                Help Center
              </Link>
            </$HelpCenterText>
            {userType === "Teacher" && (
              <$IntegrationResourcesText size="bodySansSerif">
                Need more tips and guides?{" "}
                <Link
                  href="https://along.org/resources/"
                  opensInNewTab
                  analyticsName="clicked_implementation_resources_link"
                  analyticsSurface={SurfaceType.GET_HELP}
                >
                  Check out our educator resources
                </Link>
              </$IntegrationResourcesText>
            )}
          </$ArticlesContainer>

          <$AdditionalHelpContainer>
            {submitted ? (
              successText
            ) : (
              <>
                <$AdditionalHelpHeading>
                  Need something else?
                </$AdditionalHelpHeading>
                <CollapsibleContainer
                  expandAnalyticsName="clicked_expand_get_help"
                  contentType="options"
                  content={[
                    <$FormContainer key="form">
                      {renderHelpForm()}
                    </$FormContainer>,
                  ]}
                  numOfPreviewCards={0}
                  buttonVariant="pill"
                  buttonText="Contact us"
                  initiallyExpanded={helpFormInitiallyExpanded}
                  onEntered={() => {
                    inputRef.current?.focus();
                  }}
                />
              </>
            )}
          </$AdditionalHelpContainer>
        </Modal>
      </VisibilityTracker>
    </AnalyticsProvider>
  );
}

export default GetHelpModal;
